import { useInfiniteQuery } from '@tanstack/react-query';
import { get } from '../api/api';
import { API_URLS_ENTITY } from '../constants/apiUrl';
import { QueryParamObject, toQueryString } from '../constants/utilityFunctions';
interface IUseFetchPreliminaryInvoices {
  withFavoriteInformation?: boolean;
  isFavorite?: boolean;
  withDefaultTransactionType?: boolean;
  withInvoicesOverdueInDays?: boolean;
  withHasSubMatters?: boolean;
  withHasLinkedTransactionTypes?: boolean;
  withShowOnlyOwnEntries?: boolean;
  relations?: string[];
  sortBy?: string[];
  extraFilters?: Record<string, string | number>;
  limit?: number;
  search?: string;
  fields?: string[];
}
interface IUseFetchPreliminaryInvoicesOptions {
  enabled?: boolean;
}

export const useFetchPreliminaryInvoices = (
  params: IUseFetchPreliminaryInvoices,
  options?: IUseFetchPreliminaryInvoicesOptions,
) => {
  const fetchPreliminaryInvoices = async ({ pageParam = 1 }) => {
    const { relations: preliminaryRelations, extraFilters: filters, ...rest } = params;
    const queryString = new URLSearchParams(
      Object.keys(rest)?.length ? toQueryString(rest as QueryParamObject) : '',
    );

    Object.entries(filters ?? {}).forEach(([key, value]) => {
      queryString.append(`filter.${key}`, String(value));
    });

    (preliminaryRelations ?? []).forEach((rel) => queryString.append('relations', rel));

    queryString.append('page', String(pageParam));
    try {
      return await get(
        `${API_URLS_ENTITY.PRELIMINARY_INVOICES}${API_URLS_ENTITY.MY}?${queryString.toString()}`,
      );
    } catch (error) {
      console.error('Error fetching preliminary invoices:', error);
      throw new Error(
        (error as { message: string }).message || 'Failed to fetch preliminary invoices',
      ); // Force error throwing
    }
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['preliminary-invoices-list', params],
    queryFn: fetchPreliminaryInvoices,
    enabled: options?.enabled,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage?.meta?.totalItems ?? 0;
      const fetchedCount = allPages.flatMap((page) => page.data).length;
      return fetchedCount < totalCount ? allPages.length + 1 : undefined;
    },
  });
  return {
    data: data?.pages.flatMap((page) => page.data) ?? [],
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  };
};
